import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { requestApi } from 'api/Api';

import { ia } from 'lib/helpers/utility';

import Configurations from 'components/Payments/Configurations';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Modal from 'components/shared/Modal/Modal';
import Popover from 'components/shared/Popovers/Popover/Popover';

import PreviewInvoicePackageModal from './PreviewInvoicePackageModal';

const getActionOptions = ({ pack, setSinglePackageDetails, onPay, onCancel }) => {
  return [
    {
      label: 'Preview Details',
      icon: 'eye-mono',
      onClick: () => setSinglePackageDetails()
    },
    {
      label: 'Pay balance',
      icon: 'import-mono',
      onClick: () => onPay()
    },
    pack.status === 'active' && {
      label: 'Cancel',
      icon: 'cancel-mono',
      onClick: () => onCancel()
    }
  ].filter(Boolean);
};

const PackagePopover = ({ data: pack }) => {
  const navigate = useNavigate();
  const { id: patientId } = useParams();

  const queryClient = useQueryClient();

  const [invoice, setInvoice] = useState(null);
  const [singlePackageDetails, setSinglePackageDetails] = useState(null);
  const [cancelInvoiceId, setCancelInvoiceId] = useState(null);

  const updateQueryData = (invoice, updatedPackageId) => {
    const quotesQueryKeys = queryClient.getQueryCache().findAll(['packages']);

    quotesQueryKeys.forEach((query) => {
      const queryKey = query.queryKey;

      queryClient.setQueryData(queryKey, (oldData) => {
        if (!ia(oldData?.invoicePackages)) {
          return;
        }

        return {
          ...oldData,
          invoicePackages: oldData.invoicePackages.map((item) => {
            return item.id === invoice.id
              ? {
                  ...item,
                  total_amount: invoice.total_amount,
                  packages: item.packages?.map((pack) =>
                    pack.id === updatedPackageId ? { ...pack, status: 'cancelled' } : pack
                  )
                }
              : item;
          })
        };
      });
    });
  };

  const cancelInvoice = async () => {
    const onSuccess = (data) => {
      const { invoice, updatedPackage } = data.invoice;

      setCancelInvoiceId(null);

      updateQueryData(invoice, updatedPackage.id);
    };

    requestApi({
      url: '/api/package/delete_invoice_package',
      params: cancelInvoiceId,
      onSuccess,
      navigate
    });
  };

  const popoverOptions = getActionOptions({
    pack,
    onPay: () => setInvoice(pack.currInvoice),
    onCancel: () =>
      setCancelInvoiceId({
        invoiceId: pack.currInvoice.id,
        packageId: pack.id
      }),
    setSinglePackageDetails: () => setSinglePackageDetails(pack)
  });

  const message =
    'Are you sure you want to cancel this invoice package? \n If you have any transactions associated with this invoice, canceling it will unallocate those transactions. The amounts will be converted into credits and reflected in the patient’s chart.';

  return (
    <>
      <Popover
        isFixed
        position="left"
        isBorder={false}
        options={popoverOptions}
        className="flex h-full items-center"
      />

      <Confirm
        handleOpen={!!cancelInvoiceId}
        handleClose={() => setCancelInvoiceId(null)}
        handleContinue={cancelInvoice}
        title="Cancel invoice package"
        message={message}
        primaryBtnTxt="Cancel package"
        secondaryBtnTxt="Keep"
        icon="calendar-remove-mono"
        variant="danger"
      />

      {singlePackageDetails ? (
        <PreviewInvoicePackageModal {...{ setSinglePackageDetails, singlePackageDetails }} />
      ) : null}

      <Modal
        isLarge
        slideFromRight
        handleOpen={!!invoice}
        title="Package Invoice"
        handleClose={() => setInvoice(false)}>
        <Configurations
          newInvoice={[{ ...(invoice || {}), selected: true, expanded: true }]}
          amount={invoice?.balance}
          patientId={patientId}
          onClose={() => setInvoice(false)}
        />
      </Modal>
    </>
  );
};

export default PackagePopover;
